import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, blankWindow } from '../lib/api.js';
import { openPrintSet } from '../lib/printSet.js';
import { useSession } from '../App.jsx';
import { day, inr, num, qty, stamp } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Field, Pill, useAsync } from '../components/ui.jsx';
import { CreditNoteForm } from './CreditNotes.jsx';
import { ROLES, can } from '../lib/roles.js';
import MarkInTally from '../components/MarkInTally.jsx';
import { openWhatsApp, preopenWindow } from '../lib/whatsapp.js';

// What the server lets each role do here (routes/sync.js, routes/statutory.js).
const CAN_SYNC = ['tenant_admin', 'accounts'];
const CAN_FILE = ['tenant_admin', 'accounts'];
const CAN_EWB = [...CAN_FILE, 'godown_operator'];

export default function InvoiceDetail() {
  const { id } = useParams();
  const role = useSession().user.role;
  const navigate = useNavigate();
  const inv = useAsync(() => api.get(`/api/invoices/${id}`), [id]);
  const tcs = useAsync(() => api.get(`/api/invoices/${id}/test-certificates`), [id]);
  const session = useSession();
  const [printing, setPrinting] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [crediting, setCrediting] = useState(false);
  const [sharing, setSharing] = useState(false);

  const openPrint = async () => {
    setError(null);
    try { await api.open(`/api/invoices/${id}/pdf`); } catch (e) { setError(e.message); }
  };

  // The invoice followed by every batch's Test Certificate, in one print job.
  const filedTcs = tcs.data ? tcs.data.batches.filter((b) => b.tc_id) : [];
  const printSet = async () => {
    setError(null);
    const win = blankWindow();
    setPrinting('Preparing…');
    try { await openPrintSet({ invoiceId: id, certificates: filedTcs, win, onProgress: setPrinting }); }
    catch (e) { setError(e.message); } finally { setPrinting(null); }
  };

  const repush = async () => {
    setBusy(true); setError(null);
    try { await api.post(`/api/sync/invoices/${id}/repush`); inv.reload(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (inv.loading && !inv.data) return <CardSkeleton lines={6} />;
  if (inv.error) return <Alert onRetry={inv.reload}>{inv.error}</Alert>;
  const i = inv.data;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb" style={{ marginBottom: 6 }}><Link to="/invoices">Tax invoices</Link> / <b>{i.invoice_number || 'Draft'}</b></div>
          <h1 className="page-title">{inr(i.grand_total)}</h1>
          <div className="page-sub">
            {i.buyer_name} · {day(i.invoice_date)} · despatched from {i.godown_name} ·
            {' '}order {i.do_number} · despatch {i.gd_number}
          </div>
        </div>
        <div className="inline">
          <button className="btn secondary" onClick={openPrint}>Print / PDF</button>
          {filedTcs.length > 0 && (
            <button className="btn secondary" disabled={Boolean(printing)} onClick={printSet}
              title="The invoice followed by the test certificate of every batch on it">
              {printing ? 'Preparing…' : `Print with ${filedTcs.length} TC${filedTcs.length === 1 ? '' : 's'}`}
            </button>
          )}
          {can(role, ROLES.COMMERCIAL) && i.status === 'issued'
            && (session.allEntities || session.entities).some((e) => e.id === i.entity_id && e.whatsapp_enabled) && (
            <WhatsAppButton docType="invoice" id={i.id} />
          )}
          {/* The server shares and mails only issued invoices, and not for the godown. */}
          {can(role, ROLES.COMMERCIAL) && i.status === 'issued' && (
            <button className="btn secondary" onClick={() => setSharing(!sharing)}>Send or share</button>
          )}
          {can(role, ROLES.RAISE_CREDIT) && i.status === 'issued' && (
            <button className="btn secondary" onClick={() => setCrediting(!crediting)}>
              {crediting ? 'Close' : 'Credit this invoice'}
            </button>
          )}
          {CAN_SYNC.includes(role) && i.tally_sync_status !== 'synced' && i.status === 'issued' && (
            <button className="btn" disabled={busy} onClick={repush}>{busy ? 'Queueing…' : 'Re-post to Tally'}</button>
          )}
          {CAN_SYNC.includes(role) && ['skipped', 'failed'].includes(i.tally_sync_status) && i.status === 'issued' && (
            <MarkInTally docType="invoice" id={i.id} onDone={inv.reload} />
          )}
        </div>
      </div>

      <Alert>{error}</Alert>
      {i.tally_error && <Alert kind="warn">Tally rejected this voucher: {i.tally_error}</Alert>}
      {i.status === 'draft' && <Alert kind="info">This invoice is a draft — it has no number and has not been posted.</Alert>}
      {i.status === 'issued' && i.tally_sync_status === 'skipped' && (
        <Alert kind="warn">
          Posting to Tally is off for this company, so this invoice was not sent. Its goods stay deducted from stock
          until you confirm it has been entered in Tally by hand.
        </Alert>
      )}
      {i.reverse_charge && (
        <Alert kind="info">Reverse charge: the buyer pays the tax shown to the government, so it is not in the total.</Alert>
      )}

      {sharing && <SharePanel invoice={i} onClose={() => setSharing(false)} />}

      {crediting && can(role, ROLES.RAISE_CREDIT) && (
        <CreditNoteForm invoiceId={id} onCancel={() => setCrediting(false)}
          onDone={(cn) => { setCrediting(false); navigate(`/credit-notes/${cn.id}`); }} />
      )}

      <div className="grid halves" style={{ marginBottom: 16 }}>
        <Card title="Document">
          <div className="row">
            <div><div className="label">Status</div><div style={{ marginTop: 4 }}><Pill value={i.status} /></div></div>
            <div><div className="label">Tally</div><div style={{ marginTop: 4 }}><Pill value={i.tally_sync_status} /></div>
              {i.tally_voucher_no && <div className="sub">voucher #{i.tally_voucher_no} · {stamp(i.tally_synced_at)}</div>}</div>
            <div><div className="label">Buyer GSTIN</div><div className="mono" style={{ marginTop: 4 }}>{i.buyer_gstin || 'Unregistered'}</div></div>
            <div><div className="label">Place of supply</div><div style={{ marginTop: 4 }}>{i.place_of_supply} · {i.is_interstate ? 'inter-state' : 'intra-state'}</div></div>
          </div>
        </Card>
        <Card title="Amounts">
          <table>
            <tbody>
              <tr><td>Taxable value</td><td className="r num">{inr(i.taxable_value)}</td></tr>
              {num(i.charges_total) > 0 && (
                <tr><td>Charges recovered</td><td className="r num">{inr(i.charges_total)}</td></tr>
              )}
              {i.is_interstate
                ? <tr><td>IGST</td><td className="r num">{inr(i.igst_amount)}</td></tr>
                : (
                  <>
                    <tr><td>CGST</td><td className="r num">{inr(i.cgst_amount)}</td></tr>
                    <tr><td>SGST</td><td className="r num">{inr(i.sgst_amount)}</td></tr>
                  </>
                )}
              {num(i.cess_amount) > 0 && <tr><td>Cess</td><td className="r num">{inr(i.cess_amount)}</td></tr>}
              <tr><td>Round off</td><td className="r num">{inr(i.round_off)}</td></tr>
              {num(i.tcs_amount) > 0 && (
                <tr><td>TCS u/s 206C(1H) @ {num(i.tcs_rate)}%</td><td className="r num">{inr(i.tcs_amount)}</td></tr>
              )}
              <tr><td><b>Grand total</b></td><td className="r num"><b>{inr(i.grand_total)}</b></td></tr>
            </tbody>
          </table>
          <div className="card-note" style={{ marginTop: 10 }}>{i.amount_in_words}</div>
        </Card>
      </div>

      <StatutoryPanel invoice={i} onChanged={inv.reload} />

      <CertificatesCard tcs={tcs} />

      <Card title="Goods, batches and chemical properties"
        note="Recorded at the moment of issue — a later correction to a batch cannot change this invoice.">
        {i.lines.map((l) => (
          <div key={l.id} style={{ marginBottom: 20 }}>
            <div className="card-head" style={{ marginBottom: 8 }}>
              <div>
                <h2 className="card-title">{l.line_no}. {l.description}</h2>
                <div className="card-note">HSN {l.hsn || '—'} · GST {l.gst_rate}%</div>
              </div>
              <div className="num muted">
                {qty(l.qty, l.uom)} × {inr(l.rate)} = <b style={{ color: 'var(--ink)' }}>{inr(l.line_total)}</b>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Batch</th><th className="r">Quantity</th><th>Mfg</th><th>Expiry</th><th>Properties as printed</th></tr></thead>
                <tbody>
                  {l.batches.map((b) => (
                    <tr key={b.id}>
                      <td className="mono">{b.batch_name}<div className="sub">{b.godown_name}</div></td>
                      <td className="r num">{qty(b.qty)}</td>
                      <td>{day(b.mfg_date)}</td>
                      <td>{day(b.expiry_date)}</td>
                      <td>
                        {(b.spec_snapshot || []).map((p) => (
                          <span key={p.code} className={`spec-chip ${p.in_spec === false ? 'out' : ''}`}>
                            {p.label}: <b>{p.value}</b>{p.uom ? ` ${p.uom}` : ''}{p.spec ? ` · ${p.spec}` : ''}
                          </span>
                        ))}
                        {(!b.spec_snapshot || b.spec_snapshot.length === 0) && <span className="muted">none recorded</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </Card>
    </>
  );
}

/**
 * Reporting to the IRP and the e-way bill, with the record of every call. An
 * e-way bill needs a vehicle or a transporter, so the form asks for what the
 * portal will ask for.
 */
function StatutoryPanel({ invoice, onChanged }) {
  const role = useSession().user.role;
  const canFile = CAN_FILE.includes(role);
  const canEwb = CAN_EWB.includes(role);
  const history = useAsync(() => api.get(`/api/invoices/${invoice.id}/statutory`), [invoice.id]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [transport, setTransport] = useState({
    vehicle_no: invoice.vehicle_no || '', transporter_id: '', distance_km: '', transport_mode: 'road',
  });

  if (invoice.status !== 'issued') return null;

  const act = async (label, fn) => {
    setBusy(label); setError(null);
    try { await fn(); onChanged(); history.reload(); }
    catch (e) { setError(e.details ? `${e.message}` : e.message); }
    finally { setBusy(null); }
  };

  return (
    <Card title="e-Invoice and e-way bill"
      note="Reported through the provider this entity is set up with; every call is kept."
      actions={<>
        {invoice.irn_status !== 'not_required' && <Pill value={invoice.irn_status === 'generated' ? 'issued' : invoice.irn_status} label={`IRN ${invoice.irn_status}`} />}
        {invoice.ewb_status !== 'not_required' && <Pill value={invoice.ewb_status === 'generated' ? 'issued' : invoice.ewb_status} label={`EWB ${invoice.ewb_status}`} />}
      </>}>
      <Alert>{error}</Alert>
      {invoice.irn_error && <Alert kind="warn">IRP: {invoice.irn_error}</Alert>}
      {invoice.ewb_error && <Alert kind="warn">e-Way bill: {invoice.ewb_error}</Alert>}

      <div className="grid halves">
        <div>
          <div className="label">e-Invoice</div>
          {invoice.irn ? (
            <>
              <div className="mono" style={{ wordBreak: 'break-all', marginTop: 6 }}>{invoice.irn}</div>
              <div className="sub">Ack {invoice.irn_ack_no} · {stamp(invoice.irn_ack_date)}</div>
              {canFile && invoice.irn_status !== 'cancelled' && (
                <button className="btn small secondary" style={{ marginTop: 10 }} disabled={busy === 'irn-cancel'}
                  onClick={() => act('irn-cancel', () => api.post(`/api/invoices/${invoice.id}/irn/cancel`, { reason: '2' }))}>
                  Cancel IRN (within 24 hours)
                </button>
              )}
            </>
          ) : (
            <div style={{ marginTop: 6 }}>
              <div className="card-note" style={{ marginBottom: 8 }}>Not reported yet.</div>
              {canFile && (
                <button className="btn" disabled={busy === 'irn'}
                  onClick={() => act('irn', () => api.post(`/api/invoices/${invoice.id}/irn`))}>
                  {busy === 'irn' ? 'Reporting…' : 'Report to the IRP'}
                </button>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="label">e-Way bill</div>
          {invoice.ewb_no ? (
            <>
              <div className="mono" style={{ marginTop: 6 }}>{invoice.ewb_no}</div>
              <div className="sub">
                Generated {stamp(invoice.ewb_date)}
                {invoice.ewb_valid_till ? ` · valid to ${stamp(invoice.ewb_valid_till)}` : ''}
              </div>
              {canEwb && invoice.ewb_status === 'generated' && (
                <div className="inline" style={{ marginTop: 10 }}>
                  <input placeholder="New vehicle number" value={transport.vehicle_no} style={{ maxWidth: 200 }}
                    onChange={(e) => setTransport({ ...transport, vehicle_no: e.target.value })} />
                  <button className="btn small secondary" disabled={busy === 'part-b'}
                    onClick={() => act('part-b', () => api.put(`/api/invoices/${invoice.id}/ewaybill/part-b`,
                      { vehicle_no: transport.vehicle_no }))}>
                    Update Part-B
                  </button>
                  {canFile && (
                    <button className="btn small ghost" disabled={busy === 'ewb-cancel'}
                      onClick={() => act('ewb-cancel', () => api.post(`/api/invoices/${invoice.id}/ewaybill/cancel`, { reason: '2' }))}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ marginTop: 6 }}>
              <div className="row">
                <Field label="Vehicle">
                  <input value={transport.vehicle_no}
                    onChange={(e) => setTransport({ ...transport, vehicle_no: e.target.value })} />
                </Field>
                <Field label="Transporter id" hint="if the vehicle is not known yet">
                  <input value={transport.transporter_id}
                    onChange={(e) => setTransport({ ...transport, transporter_id: e.target.value })} />
                </Field>
                <Field label="Distance (km)">
                  <input className="num" value={transport.distance_km}
                    onChange={(e) => setTransport({ ...transport, distance_km: e.target.value })} />
                </Field>
              </div>
              {canEwb && (
                <button className="btn" disabled={busy === 'ewb'}
                  onClick={() => act('ewb', () => api.post(`/api/invoices/${invoice.id}/ewaybill`, {
                    vehicle_no: transport.vehicle_no || undefined,
                    transporter_id: transport.transporter_id || undefined,
                    transport_mode: transport.transport_mode,
                    distance_km: transport.distance_km ? Number(transport.distance_km) : undefined,
                  }))}>
                  {busy === 'ewb' ? 'Generating…' : 'Generate e-way bill'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {history.data && history.data.events.length > 0 && (
        <>
          <hr className="hairline" />
          <div className="label" style={{ marginBottom: 8 }}>Portal calls</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>Action</th><th>Provider</th><th>Result</th><th>By</th></tr></thead>
              <tbody>
                {history.data.events.map((ev) => (
                  <tr key={ev.id}>
                    <td>{stamp(ev.created_at)}</td>
                    <td>{ev.action.replace(/_/g, ' ')}</td>
                    <td>{ev.provider}</td>
                    <td>{ev.status === 'ok' ? <Pill value="issued" label="ok" /> : <Pill value="failed" label="failed" />}
                      {ev.error && <div className="sub">{ev.error}</div>}</td>
                    <td>{ev.by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

/** Email the invoice with its certificates, or hand the buyer a link. */
function SharePanel({ invoice, onClose }) {
  const session = useSession();
  const entity = (session.allEntities || session.entities).find((e) => e.id === invoice.entity_id);
  const links = useAsync(() => api.get(`/api/share/invoice/${invoice.id}`), [invoice.id]);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [includeCoa, setIncludeCoa] = useState(true);
  const [result, setResult] = useState(null);
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await api.post(`/api/invoices/${invoice.id}/email`, {
        to: to.split(',').map((x) => x.trim()).filter(Boolean),
        include_coa: includeCoa,
        message: message || undefined,
      });
      setResult(res);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const share = async () => {
    setError(null);
    try {
      const res = await api.post(`/api/share/invoice/${invoice.id}`, { expires_in_days: 30 });
      setLink(res); links.reload();
    } catch (e) { setError(e.message); }
  };

  return (
    <Card title="Send or share" note="The test certificate for every batch on this invoice travels with it."
      actions={<button className="btn small ghost" onClick={onClose}>Close</button>}>
      <Alert>{error}</Alert>
      {result && (
        <Alert kind="ok">
          Queued for {result.email.to_addresses.join(', ')} with {result.email.attachments.length} attachment
          {result.email.attachments.length === 1 ? '' : 's'}. It goes out on the next send.
          {result.missing_certificates && result.missing_certificates.length > 0
            && ` No test certificate is filed for ${result.missing_certificates.join(', ')}, so it went without one.`}
        </Alert>
      )}
      <div className="row">
        <Field label="Email to" hint="comma separated">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="purchase@buyer.example" />
        </Field>
        <Field label="Message">
          <input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="As discussed." />
        </Field>
        <Field label="Attach test certificates">
          <select value={String(includeCoa)} onChange={(e) => setIncludeCoa(e.target.value === 'true')}>
            <option value="true">Yes, one per batch number</option>
            <option value="false">Invoice only</option>
          </select>
        </Field>
      </div>
      <div className="inline">
        <button className="btn" disabled={busy || !to} onClick={send}>{busy ? 'Queueing…' : 'Queue the email'}</button>
        <button className="btn secondary" onClick={share}>Create a link instead</button>
        {entity && entity.whatsapp_enabled && <WhatsAppButton docType="invoice" id={invoice.id} onShared={links.reload} />}
      </div>

      {link && (
        <Alert kind="info">
          Anyone with this link can read the invoice until it expires or is withdrawn:
          <div className="mono" style={{ marginTop: 6, userSelect: 'all', wordBreak: 'break-all' }}>{link.url}</div>
        </Alert>
      )}

      {links.data && links.data.links.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr><th>Link</th><th>Created by</th><th>Expires</th><th className="r">Views</th><th /></tr></thead>
            <tbody>
              {links.data.links.map((l) => (
                <tr key={l.id}>
                  <td>{l.revoked_at ? <span className="muted">withdrawn</span> : 'active'}</td>
                  <td>{l.created_by_name || '—'}</td>
                  <td>{l.expires_at ? day(l.expires_at) : 'never'}</td>
                  <td className="r num">{l.views}</td>
                  <td className="r">
                    {!l.revoked_at && (
                      <button className="btn small ghost"
                        onClick={async () => {
                          setError(null);
                          try { await api.post(`/api/share/${l.id}/revoke`); links.reload(); }
                          catch (e) { setError(e.message); }
                        }}>
                        Withdraw
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Opens WhatsApp with the document's link and the company's message filled in. */
export function WhatsAppButton({ docType, id, small = false, onShared }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const go = async () => {
    setBusy(true); setError(null); setNote(null);
    const win = preopenWindow();
    try {
      const r = await api.post(`/api/share/${docType}/${id}/whatsapp`, {});
      openWhatsApp(r.phone, r.text, win);
      const notes = [];
      if (!r.phone) notes.push('No mobile number for this buyer: choose the chat in WhatsApp, and add the number under Customers.');
      if (r.missing_certificates && r.missing_certificates.length) notes.push(`No test certificate filed for ${r.missing_certificates.join(', ')}.`);
      if (notes.length) setNote(notes.join(' '));
      if (onShared) onShared();
    } catch (e) {
      if (win) win.close();
      setError(e.message);
    } finally { setBusy(false); }
  };
  return (
    <>
      <button className={`btn secondary${small ? ' small' : ''}`} disabled={busy} onClick={go}>
        {busy ? 'Opening…' : 'Share on WhatsApp'}
      </button>
      {note && <span className="sub">{note}</span>}
      {error && <span className="sub" role="alert" style={{ color: 'var(--critical-text)' }}>{error}</span>}
    </>
  );
}

/** Which batch numbers on the invoice have a Test Certificate filed, and which do not. */
function CertificatesCard({ tcs }) {
  if (tcs.error) return <Alert onRetry={tcs.reload}>{tcs.error}</Alert>;
  if (!tcs.data || tcs.data.batches.length === 0) return null;
  const rows = tcs.data.batches;
  const missing = rows.filter((b) => !b.tc_id).length;
  return (
    <Card title="Test certificates"
      note={missing
        ? `${missing} of ${rows.length} batch number${rows.length === 1 ? '' : 's'} on this invoice ${missing === 1 ? 'has' : 'have'} no certificate filed yet.`
        : 'Every batch on this invoice has its certificate; they go with the email, the WhatsApp message and the print set.'}
      actions={missing > 0 ? <Link className="btn small secondary" to="/test-certificates">Upload certificates</Link> : null}>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Batch</th><th>Item</th><th className="r">Quantity</th><th>Certificate</th></tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.batch_key}>
                <td className="mono">{b.batch_no}</td>
                <td>{b.items.join(', ')}</td>
                <td className="r num">{qty(b.qty)}</td>
                <td>
                  {b.tc_id ? (
                    <button className="btn small ghost" onClick={() => api.open(`/api/test-certificates/${b.tc_id}/file`).catch(() => {})}>
                      {b.tc_filename}
                    </button>
                  ) : <span className="pill warn">not filed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
